// @flow
import React from 'react'
import Button from '@material-ui/core/Button'
import Snackbar from '@material-ui/core/Snackbar'
import TextField from '@material-ui/core/TextField'
import { compare } from 'er2_web_utils'
import { getLayers, getLayer } from 'catena/er2_map_userlayers/js/utils'
import * as ids from '../constants/ids'
import { getActiveTool } from '../components/geo_bar_panel'

type Props = {
    actions: {
        onChangeInput: Function,
        onClickInlineHelp: Function,
        onGeoBarFetchErrorRendered: Function,
        onGeoBarResultRendered: Function,
        onGeoBarSubmitBuffer: Function,
        onGeoBarSelectLayer: Function,
    },
    mapsources: object,
    theme?: object,
}

function isValid(tool) {
    return tool.expression.length > 0
}

function calculate(props) {
    const tool = getActiveTool(props)
    const layers = getLayers(props.mapsources).filter(lyr => lyr.layer_type && lyr.layer_type.toLowerCase() === 'raster')
        .sort((a, b) => compare(a.name, b.name)).map((lyr, idx) => ({
            ...lyr,
            variable: String.fromCharCode(65 + idx),
        }))
    props.actions.onGeoBarSubmitCalculate(layers, tool.expression)
}

function isValidExpression(distance: number, dispatch, getState) {
    // TODO
    return true
}

class GeoBarCalculator extends React.Component<Props> {
    renderNotifications(theme) {
        const tool = getActiveTool(this.props)
        const layerName = tool.result ? tool.result.layerName : ''
        const message = layerName ? `Result saved to ${layerName}` : 'No result found for this operation'
        return (<div>
            <Snackbar
                open={tool.resultRendered === false}
                autoHideDuration={6000}
                onClose={() => {
                    this.props.actions.onGeoBarResultRendered(tool.id)
                    if (this.props.actions.onUpdateUserLayers) this.props.actions.onUpdateUserLayers()
                }}
                message={<span id="message-id">{message}</span>}
            />
            <Snackbar
                open={tool.fetchError}
                autoHideDuration={6000}
                onClose={() => this.props.actions.onGeoBarFetchErrorRendered(tool.id)}
                message={<span id="message-id">Error performing operation</span>}
            />
        </div>)
    }

    renderTool(theme) {
        const tool = getActiveTool(this.props)
        const layers = getLayers(this.props.mapsources)
        return (<React.Fragment>
            <label className={theme.subHeaderLabel}>Variables
                <div className={theme.layersListCtr}>
                    {layers.filter(lyr => lyr.layer_type && lyr.layer_type.toLowerCase() === 'raster').sort((a, b) => compare(a.name, b.name)).map((layer, idx) => (
                        <div className={theme.layerGroup} key={layer.id}>
                            <span className={theme.label}>{String.fromCharCode(idx + 65)}: {layer.name}</span>
                        </div>
                    ))}
                </div>
            </label>
            <div className={theme.controlGroup}>
                <TextField
                    id={ids.GEO_BAR_CALCULATOR_EXPRESSION}
                    className={theme.textField}
                    label="Expression"
                    helperText="Use any variables, algebraic operators (+-/*) or scientific functions"
                    value={tool.expression}
                    onChange={evt => this.props.actions.onChangeInput(evt.target.value, ids.GEO_BAR_CALCULATOR_EXPRESSION, isValidExpression)}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    margin="normal"
                />
            </div>
            <div className={theme.formGroup}>
                <Button
                    classes={{
                        root: theme.buttonSubmit,
                    }}
                    disabled={!isValid(tool)}
                    onClick={() => calculate(this.props)}
                >
                    {!tool.isFetching &&
                    <span>Submit</span>
                    }
                    {tool.isFetching &&
                    <span><i className="fa fa-refresh fa-spin" /> Running...</span>
                    }
                </Button>
            </div>
        </React.Fragment>)
    }

    render() {
        const theme = this.props.theme
        const tool = getActiveTool(this.props)
        const helpActive = tool.helpActive

        return (<div className={theme.geoBarCtr}>
            <div className={theme.formGroup}>
                <label className={theme.toolLabel}>Raster Calculator
                    <i
                        className={`${theme.helpIcon}
                        ${helpActive ? theme.helpIconActive : ''}
                        fa fa-question-circle`}
                        onClick={() => this.props.actions.onClickInlineHelp(ids.GEO_BAR_CALCULATOR)}
                        role="button"
                        tabIndex={0}
                    />
                </label>
            </div>
            {helpActive &&
            <div className={theme.helpInlineCtr}>
                <div className={theme.helpInline}>
                    <p>
                        Perform an arithmetic operation on a set of one or more rasters.
                    </p>
                    <p>
                        All available layers below will be assigned a variable, such as &quot;A&quot; or &quot;B.&quot;
                        Use these variables, along with arithmetic operators (+&minus;&divide;*&lowast; and other supported operators,
                        to build a mathematical expression. This expression will transform existing layers into a
                        new one with the provided logic.
                    </p>
                    <p>
                        Note that additional scientific expressions are also supported.  See&nbsp;
                        <a href="https://docs.scipy.org/doc/numpy-1.13.0/reference/routines.math.html">this link</a>&nbsp;for details.
                    </p>
                </div>
            </div>
            }
            <div className={theme.toolCtr}>
                {this.renderTool(theme)}
            </div>
            {this.renderNotifications(theme)}
        </div>)
    }
}

export default GeoBarCalculator
